/** Icon stack, icon buttons (rest/hover), tooltips, and group dropdowns. */
export const ICONS_CSS = `
    .pw-icon-stack {
      position: absolute; bottom: 84px; right: 9px;
      display: flex; flex-direction: column; align-items: center; gap: 2px;
      pointer-events: auto;
      /*
       * .pw-root is pointer-events:none, so the only hoverable regions are the
       * FAB zone (top edge at 84px: 16px bottom + 68px height) and this stack.
       * The icons sit at bottom:102px; leaving the box there left an 18px dead
       * gap between them, so travelling from the × up to the icons fired a
       * spurious mouseleave and the 120ms leaveTimer hid the stack mid-move
       * (SCRIBE-423 follow-up). Drop the box to meet the FAB zone (bottom:84px)
       * and push the icons back up with padding so they don't move visually —
       * the padding keeps the hover corridor continuous.
       */
      padding-bottom: 18px;
    }
    .pw-root.pw-recording .pw-icon-stack {
      pointer-events: none;
    }
    .pw-root.pw-recording .pw-icon-stack .pw-ic.visible {
      pointer-events: auto;
    }
    /* Live perio: collapse idle siblings so the pinned leaf sits on the FAB
     * the way the notes red mic does — no empty 50px gap from Voice/etc. */
    .pw-root.pw-recording-perio .pw-ic:not(.visible) {
      display: none;
    }

    .pw-ic {
      position: relative; display: flex; align-items: center; justify-content: center;
      opacity: 0; transform: translateY(10px) scale(0.78);
      transition: opacity 0.15s ease, transform 0.5s cubic-bezier(0.2, 2.2, 0.4, 1);
      pointer-events: none;
      width: 50px; height: 50px;
    }
    .pw-ic.visible { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
    .pw-ic.pw-hidden { display: none; }
    .pw-ic[data-divider="before"] {
      margin-top: 9px;
    }
    .pw-ic-divider {
      position: absolute;
      top: -6px;
      left: 50%;
      transform: translateX(-50%);
      width: 15px;
      height: 1px;
      background: #000000;
      opacity: 0.2;
      pointer-events: none;
    }
    .pw-ic:nth-child(1) { transition-delay: 455ms; }
    .pw-ic:nth-child(2) { transition-delay: 390ms; }
    .pw-ic:nth-child(3) { transition-delay: 325ms; }
    .pw-ic:nth-child(4) { transition-delay: 260ms; }
    .pw-ic:nth-child(5) { transition-delay: 195ms; }
    .pw-ic:nth-child(6) { transition-delay: 130ms; }
    .pw-ic:nth-child(7) { transition-delay: 65ms; }
    .pw-ic:nth-child(8) { transition-delay: 0ms; }

    .pw-ic-btn {
      width: 38.4px; height: 38.4px; border-radius: 9999px;
      border: 1.2px solid transparent;
      background:
        var(--pw-fill-icon) padding-box,
        linear-gradient(transparent, transparent) padding-box,
        linear-gradient(transparent, transparent) border-box;
      box-shadow: var(--pw-shadow-rest);
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; outline: none;
      transition: width 0.18s ease, height 0.18s ease, box-shadow 0.18s ease;
      padding: 0;
    }
    .pw-ic:hover .pw-ic-btn,
    .pw-ic.pw-open .pw-ic-btn,
    .pw-ic-btn:focus-visible {
      width: 50px; height: 50px;
      background:
        var(--pw-gradient-overlay) padding-box, 
        var(--pw-fill-button) padding-box, 
        var(--pw-border-gradient) border-box;
      box-shadow: var(--pw-shadow-hover);
    }
    .pw-ic-btn img {
      width: 28px; height: 28px;
      object-fit: contain;
      transition: width 0.18s ease, height 0.18s ease;
      pointer-events: none;
      user-select: none;
    }
    .pw-ic:hover .pw-ic-btn img,
    .pw-ic.pw-open .pw-ic-btn img,
    .pw-ic-btn:focus-visible img {
      width: 36.43px; height: 36.43px;
    }
    .pw-ic[data-action="tooth"] .pw-ic-btn img {
      width: 18.77px; height: 18.77px;
    }
    .pw-ic[data-action="tooth"]:hover .pw-ic-btn img,
    .pw-ic[data-action="tooth"].pw-open .pw-ic-btn img,
    .pw-ic[data-action="tooth"] .pw-ic-btn:focus-visible img {
      width: 24.44px; height: 24.44px;
    }

    .pw-ic[data-action="voice"] .pw-ic-btn img {
      width: 18.77px; height: 18.77px;
    }
    .pw-ic[data-action="voice"]:hover .pw-ic-btn img,
    .pw-ic[data-action="voice"].pw-open .pw-ic-btn img,
    .pw-ic[data-action="voice"] .pw-ic-btn:focus-visible img {
      width: 24.44px; height: 24.44px;
    }

    .pw-ic[data-action="perio"] .pw-ic-btn img {
      width: 18.77px; height: 18.77px;
    }
    .pw-ic[data-action="perio"]:hover .pw-ic-btn img,
    .pw-ic[data-action="perio"].pw-open .pw-ic-btn img,
    .pw-ic[data-action="perio"] .pw-ic-btn:focus-visible img {
      width: 24.44px; height: 24.44px;
    }

    .pw-ic[data-action="insurance"] .pw-ic-btn img {
      width: 27.6px; height: 28.4px;
    }
    .pw-ic[data-action="insurance"]:hover .pw-ic-btn img,
    .pw-ic[data-action="insurance"].pw-open .pw-ic-btn img,
    .pw-ic[data-action="insurance"] .pw-ic-btn:focus-visible img {
      width: 36px; height: 37px;
    }

    .pw-tip {
      position: absolute; right: calc(100% + 8px); top: 50%;
      transform: translateY(-50%);
      background: var(--pw-icon-bg);
      color: var(--pw-text);
      font-family: var(--pw-font);
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
      letter-spacing: 0;
      padding: 4px 6.82667px;
      border-radius: 9999px;
      box-shadow: var(--pw-shadow-rest);
      white-space: nowrap; pointer-events: none;
      opacity: 0; transition: opacity 0.15s ease;
    }
    .pw-ic.pw-leaf:hover .pw-tip,
    .pw-ic.pw-leaf .pw-ic-btn:focus-visible ~ .pw-tip { opacity: 1; }
    .pw-ic.pw-group .pw-tip { display: none; }

    .pw-dropdown {
      position: absolute;
      top: 50%; right: 100%;
      transform: translateY(-50%);
      padding-right: 8px;
      display: none;
      pointer-events: auto;
    }
    .pw-ic.pw-group:hover .pw-dropdown,
    .pw-ic.pw-group.pw-open .pw-dropdown {
      display: block;
    }
    .pw-dropdown-panel {
      width: 144px;
      border-radius: 8px;
      border: 1.2px solid transparent;
      background:
        var(--pw-fill-panel) padding-box,
        var(--pw-border-gradient) border-box;
      box-shadow: var(--pw-shadow-dropdown);
      padding: 0;
      overflow: hidden;
      color: var(--pw-text);
    }

    .pw-dropdown-header {
      display: flex; align-items: center;
      width: 100%; height: 32px;
      padding: 0 12px;
      font-family: var(--pw-font);
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
      color: var(--pw-text);
      border-bottom: 1px solid var(--pw-divider);
      user-select: none;
    }

    .pw-dropdown-item {
      display: flex; align-items: center; gap: 8px;
      width: 100%; height: 32px;
      padding: 0 12px;
      background: transparent;
      border: 1.2px solid transparent;
      color: inherit;
      font-family: var(--pw-font);
      font-size: 12px;
      line-height: 16px;
      font-weight: 400;
      text-align: left;
      cursor: pointer;
    }
    .pw-dropdown-item + .pw-dropdown-item {
      border-top: 1px solid var(--pw-divider);
    }
    .pw-dropdown-item:hover,
    .pw-dropdown-item:focus-visible {
      border: 1.2px solid transparent;
      background:
        var(--pw-gradient-overlay) padding-box, 
        var(--pw-fill-row-hover) padding-box;
      outline: none;
    }
    .pw-dropdown-item img {
      width: 16px; height: 16px; flex-shrink: 0;
      object-fit: contain;
      pointer-events: none;
    }
    .pw-dropdown-item-label {
      flex: 1; min-width: 0;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
`;
